import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  isReservedUsername,
  UnsupportedGpxSegmentsError,
  type Activity,
  type GeometryResponse,
  type MediaAssetPublic,
  type RouteLinePoint,
  type RoutePublic,
  type Waypoint,
  type WaypointKind,
} from '@xingroute/schema'
import { ApiError, api } from '../../api/client.js'
import { useAuth } from '../../auth/AuthContext.js'
import { useLocale } from '../../locale/LocaleContext.js'
import { friendlyError } from '../../lib/friendlyError.js'
import { withConflictRetry } from '../../lib/conflictRetry.js'
import { Badge } from '../../components/ui/Misc.js'
import { Button } from '../../components/ui/Button.js'
import { Input, Select, Textarea } from '../../components/ui/Field.js'
import { IconPencil, IconTrash } from '../../components/ui/IconButton.js'
import { ActionMenu } from '../../components/ui/ActionMenu.js'
import { CoverStage, type Stage } from '../../components/CoverStage.js'
import { AuthorizedMediaImage } from '../../components/AuthorizedMediaImage.js'
import { FavoriteHeartButton } from '../../components/FavoriteHeartButton.js'
import { GpxFileActions } from '../../components/GpxRail.js'
import { WaypointList } from '../../components/WaypointList.js'
import { OfflineTileButton } from '../../components/OfflineTileDialog.js'
import { RouteCollabPanel } from '../../components/RouteCollabPanel.js'
import { EditDiffPreview } from '../../components/EditDiffPreview.js'
import {
  commitAllEdits,
  hasMetadataChanges,
  hasWaypointChanges,
  type DraftMediaChanges,
  type DraftMediaItem,
} from '../../lib/routeEditSession.js'
import type { MapStyle } from '../../components/RouteMap.js'
import { useToast } from '../../components/Toast.js'
import { EditorTagBar } from '../../components/editor/EditorTagBar.js'
import { EditableMediaSection } from '../../components/editor/EditableMediaSection.js'
import { WaypointKindPicker } from '../../components/editor/WaypointKindPicker.js'
import { EditorTrackSection, type TrackMode } from '../../components/editor/EditorTrackSection.js'
import { TrackDrawPanel } from '../../components/editor/TrackDrawPanel.js'
import { useTrackDrawState } from '../../components/editor/useTrackDrawState.js'
import { DrawingMap } from '../../components/editor/DrawingMap.js'
import { drawnRequestToGpxFile } from '../../lib/drawnGeometryGpx.js'
import {
  newWaypointId,
  type EditableWaypoint,
  type WaypointReplace,
  waypointToReplaceRequest,
  waypointsEqual,
  computeMergedRows,
} from '../../lib/mergedWaypoints.js'
import {
  canApprovePublishRoute,
  canDirectEditRoute,
  canShowPublishDirectRoute,
} from '../../lib/routePermissions.js'
import {
  formatSeasonalityForPreview,
  RouteSeasonalityBlock,
} from '../../components/RouteSeasonalityBlock.js'
import { ShareRouteModal } from '../../components/ShareCard.js'
import { WeatherStrip } from '../../components/WeatherStrip.js'
import { useRoutePresence } from '../../hooks/useRoutePresence.js'
import { parseGpx, reverseGpxTrackOrder, smoothPathElevation, trackStats } from '../../lib/gpx.js'
import { reverseGeometry } from '../../lib/reverseGeometry.js'
import { groupMediaByWaypoint } from '../../lib/groupMediaByWaypoint.js'
import {
  openCommandPalette,
  useShortcuts,
  type StylePresetShortcut,
} from '../../hooks/useShortcuts.js'
import { initialRouteDetailState, routeDetailReducer } from './state.js'
import { RouteElevationSection } from './RouteFactsSection.js'
import { useRouteDetailLoader } from './useRouteDetailLoader.js'

// RouteDetailPage — desktop full-viewport layout: left CoverStage (map + media
// via segmented switch, >= 50% width), right column with intro + GPX &
// waypoints. Mobile (< lg) falls back to normal document flow so the single
// column scrolls as one page.

/** Normalize a free-form summary for display:
 *  - Strip per-line trailing whitespace.
 *  - Collapse any run of two or more consecutive newlines into a single
 *    newline. The render is therefore flush — paragraphs sit on consecutive
 *    lines with no visual blank line between them — which keeps long
 *    descriptions from looking sparse on the route detail intro.
 *  - Trim leading / trailing blank lines.
 *  This is a render-time cosmetic pass; the raw value is preserved on disk
 *  so authors editing the field don't see their formatting altered. */
function normalizeDescription(text: string): string {
  return text
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => line.replace(/[ \t]+$/, ''))
    .join('\n')
    .replace(/\n{2,}/g, '\n')
    .trim()
}

export function RouteDetailPage() {
  const params = useParams<{ id?: string; slug?: string }>()
  const auth = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { t, locale } = useLocale()
  const { t: drawT } = useTranslation('common')
  const { showToast } = useToast()

  const [state, dispatch] = useReducer(routeDetailReducer, initialRouteDetailState)
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(() => new Set())
  const [shareOpen, setShareOpen] = useState(false)
  const [uploadingMedia, setUploadingMedia] = useState(false)
  const [committingWaypoints, setCommittingWaypoints] = useState(false)
  // 替换 GPX is now staged (committed on Save), so there is no separate in-flight
  // upload phase; kept as a constant so the existing disabled-state wiring holds.
  const uploadingGpx = false
  const [addingWaypoint, setAddingWaypoint] = useState(false)
  const [addWaypointKindPickerOpen, setAddWaypointKindPickerOpen] = useState(false)
  const [pendingAddWaypointKind, setPendingAddWaypointKind] = useState<WaypointKind | null>(null)
  const [draftWaypoints, setDraftWaypoints] = useState<EditableWaypoint[] | null>(null)
  const [draftGpxFile, setDraftGpxFile] = useState<File | null>(null)
  const [draftTrackReversed, setDraftTrackReversed] = useState(false)
  // Parsed line of a staged 替换 GPX, used to preview the replacement track
  // before Save (no network call until Save). Cleared when the draft changes.
  const [replacePreviewLine, setReplacePreviewLine] = useState<RouteLinePoint[] | null>(null)
  const [stagedDrawPreview, setStagedDrawPreview] = useState<GeometryResponse | null>(null)
  const [drawingBlankPreview, setDrawingBlankPreview] = useState(false)
  const [preDrawTrackDraft, setPreDrawTrackDraft] = useState<{
    draftGpxFile: File | null
    draftTrackReversed: boolean
    replacePreviewLine: RouteLinePoint[] | null
    stagedDrawPreview: GeometryResponse | null
  } | null>(null)
  const [trackMode, setTrackMode] = useState<TrackMode>('upload')
  const [draftMediaChanges, setDraftMediaChanges] = useState<DraftMediaChanges>({
    added: [],
    removed: [],
  })
  const [regionDetecting, setRegionDetecting] = useState(false)
  const [aiAssisting, setAiAssisting] = useState(false)
  const [autoCandidates, setAutoCandidates] = useState<EditableWaypoint[]>([])
  const [selectedAutoIds, setSelectedAutoIds] = useState<Set<string>>(new Set())
  const {
    draft,
    mapWaypointState,
    focusWaypoint,
    activeWaypoint,
    pinnedWaypoints,
    trackReversed,
    focusMedia,
    showWaypointsOnMap,
    rightTab,
  } = state

  const loader = useRouteDetailLoader({ id: params.id, slug: params.slug })
  const location = useLocation()
  const { route, etag, media, geometry: serverGeometry, loading, geometryError } = loader
  const error = loader.error
  const consumedCollabDeepLink = useRef<string | null>(null)

  const reloadEtag = useCallback(async (): Promise<string> => {
    const id = route?.id ?? params.id ?? ''
    const result = await api.getRoute(id, auth.status === 'authed' ? auth.token : null)
    loader.setRoute(result.route, result.etag)
    return result.etag ?? result.route.etag
  }, [route?.id, params.id, auth, loader])
  const editing = state.editing
  const geometryLoading = loading && !serverGeometry
  const canEdit = auth.status === 'authed'
  const editorOpen = editing
  const toggleTrackReversed_ = useCallback(() => {
    dispatch({ type: 'track-reversed/toggle' })
  }, [])
  const isAdmin = auth.status === 'authed' && auth.user?.role === 'admin'
  const isCreatorOrAdmin =
    auth.status === 'authed' && route != null && (auth.user?.id === route.created_by || isAdmin)
  const isCreatorPendingReview =
    auth.status === 'authed' &&
    route != null &&
    auth.user?.id === route.created_by &&
    route.status === 'pending_review' &&
    !canApprovePublishRoute(auth.user, route)
  const isCreatorDraft =
    auth.status === 'authed' &&
    route != null &&
    auth.user?.id === route.created_by &&
    route.status === 'draft'
  const routeFrozenForPublicationReview = route?.status === 'pending_review'
  const canOpenEditor = canEdit && !routeFrozenForPublicationReview && !isCreatorPendingReview
  const canRouteDirectEdit =
    auth.status === 'authed' && auth.user != null && route != null
      ? canDirectEditRoute(auth.user, route)
      : false
  const canEditSeasonality = canRouteDirectEdit
  const shareUrl = useMemo(() => routeShareUrl(route), [route?.slug])
  const [coverStage, setCoverStage] = useState<Stage>('map')
  const [savePreview, setSavePreview] = useState<{
    mode: 'review' | 'publish' | 'request'
    draftWaypoints: EditableWaypoint[] | null
  } | null>(null)
  const [savingEdit, setSavingEdit] = useState(false)
  const savingEditRef = useRef(false)
  const consentAttemptRef = useRef(0)
  const routeMutationRefreshSeq = useRef(0)
  const navigationGenerationRef = useRef(0)
  const routeTargetKey = params.id ? `id:${params.id}` : `slug:${params.slug ?? ''}`
  const routeTargetRef = useRef(routeTargetKey)
  if (routeTargetRef.current !== routeTargetKey) {
    routeTargetRef.current = routeTargetKey
    navigationGenerationRef.current += 1
    consentAttemptRef.current += 1
  }
  const currentRouteIdRef = useRef<string | null>(route?.id ?? null)
  currentRouteIdRef.current = route?.id ?? null
  const [publicationConsent, setPublicationConsent] = useState<{
    route: RoutePublic
    etag: string
    approveAfterRequest: boolean
    attemptId: number
    targetKey: string
    navigationGeneration: number
  } | null>(null)
  const [publicationAccepted, setPublicationAccepted] = useState(false)
  const draftMediaChangesRef = useRef(draftMediaChanges)
  const autoWaypointRequestSeq = useRef(0)
  const previousAuthUserId = useRef<string | null>(
    auth.status === 'authed' ? (auth.user?.id ?? null) : null,
  )
  const [shortcutHelpOpen, setShortcutHelpOpen] = useState(false)
  const [activeMapStyle, setActiveMapStyle] = useState<MapStyle>('standard')
  const [mapStyleRequest, setMapStyleRequest] = useState<{
    style: MapStyle
    nonce: number
  } | null>(null)
  const presenceUsers = useRoutePresence(route?.id ?? null, { editing })
  const editingPresenceUser = presenceUsers.find((u) => u.editing && u.user_id !== auth.user?.id)

  // In-place hand-draw redraw of an existing route's track. Direct editors can
  // stage a replacement geometry on an empty canvas and commit it through the
  // same Save path used by a manual GPX replace. Works on a geometry-less route too.
  const drawActivity: Activity = route?.activities?.[0] ?? 'hiking'
  const canDrawTrack = editing && canRouteDirectEdit
  const trackDrawActive = canDrawTrack && trackMode === 'draw'

  useEffect(() => {
    setPublicationAccepted(false)
  }, [
    route?.etag,
    draft.title,
    draft.summary,
    draft.difficulty,
    draft.route_type,
    draft.activities,
    draft.region,
    draft.natural_features,
    draft.seasonality,
    draftGpxFile,
    draftTrackReversed,
    draftWaypoints,
    draftMediaChanges,
  ])

  useEffect(() => {
    savingEditRef.current = false
    setSavingEdit(false)
    setPublicationConsent(null)
    setPublicationAccepted(false)
  }, [routeTargetKey])

  function isCurrentRoute(routeId: string, targetKey: string, navigationGeneration: number) {
    return (
      navigationGenerationRef.current === navigationGeneration &&
      routeTargetRef.current === targetKey &&
      currentRouteIdRef.current === routeId
    )
  }

  function isCurrentConsentAttempt(
    attemptId: number,
    routeId: string,
    targetKey: string,
    navigationGeneration: number,
  ) {
    return (
      consentAttemptRef.current === attemptId &&
      isCurrentRoute(routeId, targetKey, navigationGeneration)
    )
  }
  const editDraw = useTrackDrawState({
    t: drawT,
    initialActivity: drawActivity,
    lockActivity: true,
    disabled: !trackDrawActive || savingEdit,
    routeSegment: auth.token
      ? async (points, profile) => {
          const result = await api.previewRoute(
            { points: points.map((p) => ({ lat: p.lat, lon: p.lon })), profile },
            auth.token!,
          )
          return result.line
        }
      : undefined,
  })
  // Captured map center for the empty draw canvas. Set from the route geometry
  // centroid when entering 手绘 (before the canvas is cleared) so the empty
  // DrawingMap stays positioned over the route.
  const [drawInitialCenter, setDrawInitialCenter] = useState<[number, number] | undefined>(
    undefined,
  )
  const enterTrackMode = useCallback(
    (next: TrackMode) => {
      if (next === 'draw') {
        // Capture the center before clearing, then start from an empty canvas.
        const line = serverGeometry?.line ?? []
        if (line.length > 0) {
          const sum = line.reduce((acc, p) => ({ lat: acc.lat + p.lat, lon: acc.lon + p.lon }), {
            lat: 0,
            lon: 0,
          })
          setDrawInitialCenter([sum.lon / line.length, sum.lat / line.length])
        }
        autoWaypointRequestSeq.current += 1
        setPreDrawTrackDraft({
          draftGpxFile,
          draftTrackReversed,
          replacePreviewLine,
          stagedDrawPreview,
        })
        setDraftGpxFile(null)
        setDraftTrackReversed(false)
        setReplacePreviewLine(null)
        setStagedDrawPreview(null)
        setDrawingBlankPreview(true)
        setAutoCandidates([])
        setSelectedAutoIds(new Set())
        setAddingWaypoint(false)
        setAddWaypointKindPickerOpen(false)
        setPendingAddWaypointKind(null)
        dispatch({ type: 'active/waypoint/clear' })
        dispatch({ type: 'focus/waypoint/clear' })
        dispatch({ type: 'pinned-waypoints/clear' })
        editDraw.reset()
      } else if (drawingBlankPreview) {
        setDrawingBlankPreview(false)
        const restoresGeometryDraft = Boolean(
          preDrawTrackDraft?.draftGpxFile || preDrawTrackDraft?.stagedDrawPreview,
        )
        if (preDrawTrackDraft) {
          setDraftGpxFile(preDrawTrackDraft.draftGpxFile)
          setDraftTrackReversed(preDrawTrackDraft.draftTrackReversed)
          setReplacePreviewLine(preDrawTrackDraft.replacePreviewLine)
          setStagedDrawPreview(preDrawTrackDraft.stagedDrawPreview)
          setPreDrawTrackDraft(null)
        }
        if (draftWaypoints === null && !restoresGeometryDraft) {
          setDraftWaypoints(
            serverGeometry ? ([...serverGeometry.points] as EditableWaypoint[]) : null,
          )
        }
      }
      setTrackMode(next)
    },
    [
      draftGpxFile,
      draftTrackReversed,
      draftWaypoints,
      drawingBlankPreview,
      editDraw,
      preDrawTrackDraft,
      replacePreviewLine,
      serverGeometry,
      stagedDrawPreview,
    ],
  )
  const finishTrackDraw = useCallback(() => {
    const request = editDraw.buildRequest()
    if (!request) return
    const file = drawnRequestToGpxFile(request, route?.slug || 'route')
    if (!file) return
    setDraftGpxFile(file)
    // Preview the just-drawn geometry until Save commits it (local-cache draft).
    setStagedDrawPreview({
      etag: serverGeometry?.etag ?? '',
      geometry_type: 'line',
      line: (request.line ?? []).map((p) => ({ lat: p.lat, lon: p.lon })),
      points: (request.points ?? []).map((p) => ({
        id: newWaypointId(),
        lat: p.lat,
        lon: p.lon,
        name: p.name || undefined,
        desc: p.desc || undefined,
        kind: p.kind ?? 'generic',
        auto: false,
      })),
    })
    // The staged geometry defines its own points; leave waypoint draft unset so
    // the preview and diff do not treat old route points as a separate deletion.
    setDraftWaypoints(null)
    setDrawingBlankPreview(false)
    setPreDrawTrackDraft(null)
    setReplacePreviewLine(null)
    setDraftTrackReversed(false)
    setTrackMode('upload')
  }, [editDraw, route?.slug, serverGeometry?.etag])

  // Local geometry draft that previews pending changes before Save. Precedence:
  // active draw → staged replace GPX → null (server/reverse handled elsewhere).
  const draftGeometryPreview = useMemo<GeometryResponse | null>(() => {
    if (!editorOpen) return null
    if (trackDrawActive) {
      const etag = serverGeometry?.etag ?? ''
      const line =
        editDraw.routedPreviewLine.length >= 2
          ? editDraw.routedPreviewLine
          : editDraw.vertices.map((v) => ({ lat: v.lat, lon: v.lng }))
      return {
        etag,
        geometry_type: 'line',
        line,
        points: editDraw.pois.map((poi) => ({
          id: newWaypointId(),
          lat: poi.lat,
          lon: poi.lng,
          name: poi.name || undefined,
          desc: poi.desc || undefined,
          kind: poi.kind,
          auto: false,
        })),
      }
    }
    if (drawingBlankPreview) {
      return {
        etag: serverGeometry?.etag ?? '',
        geometry_type: 'line',
        line: [],
        points: [],
      }
    }
    if (draftGpxFile && replacePreviewLine && replacePreviewLine.length > 0) {
      return {
        etag: serverGeometry?.etag ?? '',
        geometry_type: 'line',
        line: replacePreviewLine,
        points: [],
      }
    }
    if (stagedDrawPreview) return stagedDrawPreview
    return null
  }, [
    editorOpen,
    trackDrawActive,
    editDraw.vertices,
    editDraw.routedPreviewLine,
    editDraw.pois,
    drawingBlankPreview,
    drawActivity,
    draftGpxFile,
    replacePreviewLine,
    stagedDrawPreview,
    serverGeometry?.etag,
  ])

  // Base geometry for the map track line + elevation. While editing, a local
  // geometry draft (draw / staged replace) takes precedence so the editor
  // previews pending geometry; otherwise the staged reverse toggle
  // (draftTrackReversed) previews the reversed line. Persisted only on Save.
  const baseGeometry = useMemo(() => {
    if (editorOpen && draftGeometryPreview) return draftGeometryPreview
    if (!serverGeometry) return null
    const wantReverse = editorOpen ? draftTrackReversed : trackReversed
    return wantReverse ? reverseGeometry(serverGeometry) : serverGeometry
  }, [serverGeometry, trackReversed, draftTrackReversed, editorOpen, draftGeometryPreview])

  // Full geometry including draft waypoints — used by map markers + waypoint
  // list. A geometry draft (draw / staged replace) supplies its own points, so
  // the draftWaypoints override only applies to plain waypoint edits.
  const geometry = useMemo(() => {
    if (!baseGeometry) return null
    if (editorOpen && !draftGeometryPreview && draftWaypoints) {
      return { ...baseGeometry, points: toGeometryPoints(draftWaypoints) }
    }
    return baseGeometry
  }, [baseGeometry, editorOpen, draftWaypoints, draftGeometryPreview])

  // Whether the previewed geometry can supply per-point elevation. A hand-drawn
  // line has none, so the elevation profile + ascent/descent hide until the
  // server regenerates them after Save.
  const previewHasElevation = useMemo(
    () => (baseGeometry?.line ?? []).some((p) => p.ele !== undefined),
    [baseGeometry],
  )

  const gpxStats = useMemo(() => {
    if (!geometry) return null
    return trackStats(
      smoothPathElevation(geometry.line.map((p) => ({ lat: p.lat, lon: p.lon, ele: p.ele }))),
    )
  }, [geometry])
  const allowRouteKpiFallback = !editorOpen || !draftGeometryPreview

  const startEditing = useCallback(() => {
    if (!canOpenEditor || !route) return
    dispatch({ type: 'draft/reset-from-route', route })
    dispatch({ type: 'right-tab/set', tab: 'intro' })
    dispatch({ type: 'editing/set', editing: true })
    setDraftWaypoints(serverGeometry ? ([...serverGeometry.points] as EditableWaypoint[]) : null)
    setDraftGpxFile(null)
    setDraftTrackReversed(false)
    setReplacePreviewLine(null)
    setStagedDrawPreview(null)
    setDrawingBlankPreview(false)
    setPreDrawTrackDraft(null)
    setTrackMode('upload')
    setDraftMediaChanges({ added: [], removed: [] })
    setCoverStage('map')
    navigate(`/routes/${route.id}?edit=1`, { replace: true })
  }, [canOpenEditor, route, serverGeometry, navigate])

  const stopEditing = useCallback(() => {
    dispatch({ type: 'editing/set', editing: false })
    setAddingWaypoint(false)
    setAddWaypointKindPickerOpen(false)
    setPendingAddWaypointKind(null)
    setDraftWaypoints(null)
    setDraftGpxFile(null)
    setDraftTrackReversed(false)
    setReplacePreviewLine(null)
    setStagedDrawPreview(null)
    setDrawingBlankPreview(false)
    setPreDrawTrackDraft(null)
    setTrackMode('upload')
    autoWaypointRequestSeq.current += 1
    // Revoke object URLs via functional update to read the latest state
    setDraftMediaChanges((prev) => {
      for (const item of prev.added) {
        URL.revokeObjectURL(item.previewUrl)
      }
      return { added: [], removed: [] }
    })
    setAutoCandidates([])
    setSelectedAutoIds(new Set())
    if (route?.slug) {
      navigate(`/r/${route.slug}`, { replace: true })
    } else {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        next.delete('edit')
        return next
      })
    }
  }, [route, navigate, setSearchParams])

  const selectRightTab = useCallback(
    (tab: 'intro' | 'history') => {
      dispatch({ type: 'right-tab/set', tab })
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (tab === 'history') {
            next.set('tab', 'collab')
          } else {
            next.delete('tab')
          }
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  useEffect(() => {
    const nextUserId = auth.status === 'authed' ? (auth.user?.id ?? null) : null
    const previousUserId = previousAuthUserId.current
    previousAuthUserId.current = nextUserId
    if (previousUserId !== null && previousUserId !== nextUserId && editing) {
      stopEditing()
    }
  }, [auth.status, auth.user?.id, editing, stopEditing])

  useEffect(() => {
    if (auth.status === 'loading' || !route) return
    const wantsEdit = searchParams.get('edit') === '1'
    const wantsCollabTab =
      searchParams.get('tab') === 'collab' || searchParams.get('tab') === 'history'
    const collabDeepLinkKey = [
      location.key,
      params.id ?? '',
      params.slug ?? '',
      searchParams.toString(),
    ].join('|')
    if (!editing && wantsCollabTab && consumedCollabDeepLink.current !== collabDeepLinkKey) {
      consumedCollabDeepLink.current = collabDeepLinkKey
      if (rightTab !== 'history') {
        dispatch({ type: 'right-tab/set', tab: 'history' })
      }
    }
    if (wantsEdit && canOpenEditor && !editing) {
      dispatch({ type: 'draft/reset-from-route', route })
      dispatch({ type: 'right-tab/set', tab: 'intro' })
      dispatch({ type: 'editing/set', editing: true })
      setDraftWaypoints(serverGeometry ? ([...serverGeometry.points] as EditableWaypoint[]) : null)
      setDraftGpxFile(null)
      setDraftTrackReversed(false)
      setReplacePreviewLine(null)
      setStagedDrawPreview(null)
      setDrawingBlankPreview(false)
      setPreDrawTrackDraft(null)
      // Honor the create-funnel ?draw=1 deep link: auto-open 手绘 on an empty
      // canvas for a direct-editable route (e.g. a freshly created blank draft).
      const wantsDraw = searchParams.get('draw') === '1' && canRouteDirectEdit && true
      if (wantsDraw) {
        enterTrackMode('draw')
      } else {
        setTrackMode('upload')
      }
      setDraftMediaChanges({ added: [], removed: [] })
      setCoverStage('map')
      return
    }
    if ((!wantsEdit || !canOpenEditor) && editing) {
      if (route.status === 'pending_review') {
        showToast({ message: t('routeDetail.pendingEditorClosed'), tone: 'info' })
        dispatch({ type: 'right-tab/set', tab: 'history' })
      }
      dispatch({ type: 'editing/set', editing: false })
    }
    if (wantsEdit && !canOpenEditor) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        next.delete('edit')
        next.delete('draw')
        if (route.status === 'pending_review') next.set('tab', 'collab')
        return next
      })
    }
  }, [
    auth.status,
    canOpenEditor,
    canRouteDirectEdit,
    enterTrackMode,
    editing,
    location.key,
    params.id,
    params.slug,
    route,
    rightTab,
    searchParams,
    setSearchParams,
    serverGeometry,
    showToast,
    t,
  ])

  // Late-init: if editing started before geometry loaded, init draft when geometry arrives
  useEffect(() => {
    if (editing && serverGeometry && draftWaypoints === null && !draftGeometryPreview) {
      setDraftWaypoints([...serverGeometry.points] as EditableWaypoint[])
    }
  }, [editing, serverGeometry, draftWaypoints, draftGeometryPreview])

  useEffect(() => {
    if (route) dispatch({ type: 'draft/reset-from-route', route })
  }, [route?.id])

  useEffect(() => {
    draftMediaChangesRef.current = draftMediaChanges
  }, [draftMediaChanges])

  useEffect(() => {
    return () => {
      for (const item of draftMediaChangesRef.current.added) {
        URL.revokeObjectURL(item.previewUrl)
      }
    }
  }, [])

  useEffect(() => {
    const currentWaypoints = geometry?.points ?? []
    const ids = new Set(currentWaypoints.map((waypoint) => waypoint.id))
    if (geometry) {
      for (const row of computeMergedRows(currentWaypoints as EditableWaypoint[], geometry.line)) {
        ids.add(row.rowKey)
      }
    }
    if (activeWaypoint && !ids.has(activeWaypoint.id)) {
      dispatch({ type: 'active/waypoint/clear' })
    }
    if (focusWaypoint && !ids.has(focusWaypoint.id)) {
      dispatch({ type: 'focus/waypoint/clear' })
    }
  }, [geometry?.points, activeWaypoint?.id, focusWaypoint?.id])

  function selectActiveWaypoint_(id: string) {
    dispatch({ type: 'active/waypoint', id })
    dispatch({ type: 'focus/waypoint', id })
  }

  function activateWaypoint_(id: string) {
    const alreadyPinned = pinnedWaypoints.includes(id)
    if (alreadyPinned) {
      dispatch({ type: 'pinned-waypoints/toggle', id })
    } else {
      dispatch({ type: 'pinned-waypoints/add', id })
    }

    if (activeWaypoint?.id === id) {
      dispatch({ type: 'active/waypoint/clear' })
      dispatch({ type: 'focus/waypoint/clear' })
    } else if (!alreadyPinned) {
      selectActiveWaypoint_(id)
    }
    dispatch({ type: 'right-tab/set', tab: 'intro' })
  }

  function focusMedia_(mediaId: string) {
    dispatch({ type: 'focus/media', mediaId })
  }

  useEffect(() => {
    if (auth.status !== 'authed' || !auth.token) {
      setFavoriteIds(new Set())
      return
    }

    let cancelled = false
    api
      .listFavorites(auth.token)
      .then((items) => {
        if (!cancelled) setFavoriteIds(new Set(items.map((item) => item.route_id)))
      })
      .catch(() => {
        if (!cancelled) setFavoriteIds(new Set())
      })
    return () => {
      cancelled = true
    }
  }, [auth.status, auth.token])

  const toggleCoverStage = useCallback(() => {
    setCoverStage((current) =>
      current === 'map' && (media.length > 0 || canEdit) ? 'gallery' : 'map',
    )
  }, [canEdit, media.length])

  // Split media into route-level vs waypoint-bound so the
  // waypoint list can show per-waypoint attachments without re-fetching.
  const groupedMedia = useMemo(() => groupMediaByWaypoint(media), [media])
  const attachmentWaypointIds = useMemo(
    () => new Set(groupedMedia.byWaypointId.keys()),
    [groupedMedia],
  )

  const setStylePreset = useCallback((preset: StylePresetShortcut) => {
    const styles: Record<StylePresetShortcut, MapStyle> = {
      1: 'standard',
      2: 'outdoor',
      3: 'satellite',
    }
    setCoverStage('map')
    setMapStyleRequest((prev) => ({
      style: styles[preset],
      nonce: (prev?.nonce ?? 0) + 1,
    }))
  }, [])

  const focusRelativeWaypoint = useCallback(
    (delta: -1 | 1) => {
      const waypoints =
        mapWaypointState.waypoints.length > 0
          ? mapWaypointState.waypoints
          : (geometry?.points ?? [])
      if (waypoints.length === 0) return
      const currentIndex = focusWaypoint
        ? waypoints.findIndex((waypoint) => waypoint.id === focusWaypoint.id)
        : -1
      const nextIndex =
        currentIndex >= 0
          ? (currentIndex + delta + waypoints.length) % waypoints.length
          : delta > 0
            ? 0
            : waypoints.length - 1
      const next = waypoints[nextIndex]
      if (!next) return
      setCoverStage('map')
      dispatch({ type: 'show-waypoints/set', show: true })
      dispatch({ type: 'right-tab/set', tab: 'intro' })
      selectActiveWaypoint_(next.id)
    },
    [focusWaypoint, geometry?.points, mapWaypointState.waypoints],
  )

  const startEditingFromShortcut = useCallback(() => {
    startEditing()
  }, [startEditing])

  const cancelShortcutSurface = useCallback(() => {
    if (shortcutHelpOpen) {
      setShortcutHelpOpen(false)
      return
    }
    if (editing) stopEditing()
  }, [editing, shortcutHelpOpen, stopEditing])

  useShortcuts({
    detail: {
      enabled: Boolean(route),
      onToggleStage: toggleCoverStage,
      onStylePreset: setStylePreset,
      onPreviousWaypoint: () => focusRelativeWaypoint(-1),
      onNextWaypoint: () => focusRelativeWaypoint(1),
      onEdit: canOpenEditor ? startEditingFromShortcut : undefined,
      onCancelEdit: cancelShortcutSurface,
      onShowCheatsheet: () => setShortcutHelpOpen(true),
      onFocusTitleSearch: openCommandPalette,
    },
  })

  // Declared before the early returns below so the hook order stays stable
  // across the route-loading transition (route starts null, then resolves).
  // `commitWaypoints` is a hoisted function declaration further down.
  const onMapWaypointsStateChange = useCallback(
    (next: { editing: boolean; waypoints: Waypoint[] }) => {
      dispatch({ type: 'map-waypoints/set', state: next })
      // Draft-only: map drag updates local draft, never commits directly.
      if (!next.editing || !state.editing) return
      setDraftWaypoints((prev) => {
        if (!prev) return prev
        if (prev && waypointsEqual(prev, next.waypoints)) return prev
        if (next.waypoints.length !== prev.length) return prev
        return next.waypoints as EditableWaypoint[]
      })
    },
    [state.editing],
  )

  useEffect(() => {
    if (!addingWaypoint) return
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setAddingWaypoint(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [addingWaypoint])

  const loadAutoWaypoints = useCallback(
    async (routeId: string) => {
      const requestId = ++autoWaypointRequestSeq.current
      setAutoCandidates([])
      setSelectedAutoIds(new Set())
      if (!auth.token || !canRouteDirectEdit) return
      try {
        const candidates = await api.previewAutoWaypoints(routeId, auth.token)
        if (requestId !== autoWaypointRequestSeq.current) return
        setAutoCandidates(
          candidates.map((candidate) => ({
            ...candidate,
            id: candidate.id ?? newWaypointId(),
          })),
        )
      } catch {
        if (requestId !== autoWaypointRequestSeq.current) return
        showToast({ message: t('editor.waypointList.autoLoadFailed'), tone: 'info' })
      }
    },
    [auth.token, canRouteDirectEdit, showToast, t],
  )

  useEffect(() => {
    if (
      !editing ||
      !canRouteDirectEdit ||
      draftGeometryPreview ||
      !route?.id ||
      !serverGeometry ||
      serverGeometry.line.length === 0
    ) {
      autoWaypointRequestSeq.current += 1
      setAutoCandidates([])
      setSelectedAutoIds(new Set())
      return
    }
    void loadAutoWaypoints(route.id)
  }, [
    canRouteDirectEdit,
    draftGeometryPreview,
    editing,
    route?.id,
    serverGeometry?.etag,
    serverGeometry?.line.length,
    loadAutoWaypoints,
  ])

  if (error) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <p className="text-sm text-[var(--xr-danger)]">{friendlyError(error, t)}</p>
      </div>
    )
  }
  if (!route) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <p className="text-sm text-[var(--xr-text-muted)]">…</p>
      </div>
    )
  }
  function draftPatch(): {
    title?: string
    summary?: string
    difficulty?: RoutePublic['difficulty']
    route_type?: RoutePublic['route_type']
    activities?: RoutePublic['activities']
    region?: RoutePublic['region']
    natural_features?: RoutePublic['natural_features']
    seasonality?: RoutePublic['seasonality']
  } {
    const raw = {
      title: draft.title,
      summary: draft.summary,
      difficulty: draft.difficulty,
      route_type: draft.route_type,
      activities: draft.activities,
      region: draft.region,
      natural_features: draft.natural_features,
      seasonality: draft.seasonality,
    }
    return Object.fromEntries(Object.entries(raw).filter(([, v]) => v != null))
  }

  function waypointSnapshot(): EditableWaypoint[] | null {
    if (drawingBlankPreview || trackDrawActive) return null
    return draftWaypoints ? draftWaypoints.map((waypoint) => ({ ...waypoint })) : null
  }
  const saveActionDisabled = savingEdit || uploadingGpx || trackDrawActive

  async function handleConfirmSave(
    publishDirect: boolean,
    waypointDraft: EditableWaypoint[] | null = draftWaypoints,
    requestReview = false,
  ) {
    if (!auth.token || !etag || !route) return
    if (savingEditRef.current) return
    const publicationAttempt =
      publishDirect || requestReview ? ++consentAttemptRef.current : undefined
    const routeId = route.id
    const targetKey = routeTargetRef.current
    const navigationGeneration = navigationGenerationRef.current
    const isCurrentSaveAttempt = () => isCurrentRoute(routeId, targetKey, navigationGeneration)
    const isCurrentPublicationAttempt = () =>
      publicationAttempt != null &&
      isCurrentConsentAttempt(publicationAttempt, routeId, targetKey, navigationGeneration)
    savingEditRef.current = true
    setSavingEdit(true)
    try {
      const metaPatch = hasMetadataChanges(draftPatch(), route) ? draftPatch() : null
      const wpChanged =
        waypointDraft && serverGeometry
          ? hasWaypointChanges(waypointDraft, serverGeometry.points)
          : false
      const mediaChanged =
        draftMediaChanges.added.length > 0 || draftMediaChanges.removed.length > 0

      if (!canRouteDirectEdit) {
        const proposalChanges: Record<string, unknown> = {}
        if (metaPatch) proposalChanges.metadata_patch = metaPatch
        if (wpChanged && waypointDraft && !draftGpxFile) {
          proposalChanges.waypoints = waypointDraft.map(stripWaypoint)
        }
        const hasProposalPayload =
          Object.keys(proposalChanges).length > 0 ||
          draftGpxFile != null ||
          draftMediaChanges.added.length > 0
        if (!hasProposalPayload) {
          showToast({
            message:
              draftMediaChanges.removed.length > 0
                ? locale === 'zh'
                  ? '建议编辑暂不支持删除既有图片；未提交建议'
                  : 'Proposal mode does not support removing existing media yet'
                : locale === 'zh'
                  ? '没有可提交的改动'
                  : 'No changes to propose',
            tone: 'info',
          })
          return
        }
        try {
          const proposal = await api.createRouteProposal(
            route.id,
            {
              changes: proposalChanges,
              base_etag: etag,
              message: locale === 'zh' ? '线路建议编辑' : 'Route edit proposal',
            },
            auth.token,
          )
          if (!isCurrentSaveAttempt()) return
          if (draftGpxFile) {
            await api.stageProposalGeometry(proposal.id, draftGpxFile, auth.token)
            if (!isCurrentSaveAttempt()) return
          }
          try {
            for (const item of draftMediaChanges.added) {
              await api.stageProposalMedia(proposal.id, item.file, auth.token)
              if (!isCurrentSaveAttempt()) return
            }
          } catch (stageErr) {
            if (!isCurrentSaveAttempt()) return
            try {
              await api.withdrawProposal(proposal.id, auth.token)
              if (!isCurrentSaveAttempt()) return
            } catch (cleanupErr) {
              if (!isCurrentSaveAttempt()) return
              throw new Error(
                `proposal cleanup failed: ${cleanupErr instanceof Error ? cleanupErr.message : String(cleanupErr)}`,
              )
            }
            throw stageErr
          }
          if (draftMediaChanges.removed.length > 0) {
            showToast({
              message:
                locale === 'zh'
                  ? '建议编辑暂不支持删除既有图片；已忽略删除项'
                  : 'Proposal mode does not support removing existing media yet',
              tone: 'info',
            })
          }
          showToast({
            message: locale === 'zh' ? '已提交建议编辑' : 'Proposal submitted',
            tone: 'success',
          })
          stopEditing()
          dispatch({ type: 'right-tab/set', tab: 'history' })
        } catch (err) {
          if (!isCurrentSaveAttempt()) return
          showToast({
            message:
              locale === 'zh'
                ? `提交建议失败：${friendlyError(err, t)}`
                : `Failed to submit proposal: ${friendlyError(err, t)}`,
            tone: 'error',
            durationMs: 8000,
          })
        }
        return
      }

      // Track reverse is staged: build the reversed GPX now and commit it
      // through the same path as a replacement file so it goes through Save.
      let gpxToCommit = draftGpxFile
      if (draftTrackReversed && !gpxToCommit && serverGeometry) {
        try {
          const reversed = reverseGeometry(serverGeometry)
          const rawGpxText = await api.getRouteGpxText(route.id, auth.token, 'raw')
          if (!isCurrentSaveAttempt()) return
          const reversedGpxText = reverseGpxTrackOrder(rawGpxText, reversed.points)
          if (!reversedGpxText) throw new Error('Unable to reverse GPX XML')
          gpxToCommit = new File([reversedGpxText], `${route.slug || 'route'}.gpx`, {
            type: 'application/gpx+xml',
          })
        } catch (err) {
          if (!isCurrentSaveAttempt()) return
          showToast({ message: t('editor.gpx.reverseFailed'), tone: 'error', durationMs: 8000 })
          return
        }
      }
      const geometryChanged = gpxToCommit != null

      let result = await commitAllEdits({
        routeId: route.id,
        token: auth.token,
        startEtag: etag,
        currentStatus: route.status,
        metadataPatch: metaPatch,
        gpxFile: gpxToCommit,
        trackReversed: draftTrackReversed,
        // A user-supplied draw/replace GPX defines the geometry points; the
        // server regenerates them on reparse, so never also PUT waypoints in
        // that same commit. A reverse-generated GPX is built only from the
        // server's own points, so it must not suppress concurrent waypoint edits.
        waypoints: draftGpxFile != null ? null : wpChanged ? waypointDraft : null,
        mediaChanges: draftMediaChanges,
        publishDirect: false,
        api: {
          patchRoute: (id, patch, token, etag) => api.patchRoute(id, patch, token, etag),
          requestPublish: (id, token, etag) => api.requestRoutePublish(id, token, etag),
          approvePublish: (id, token, etag) => api.approveRoutePublish(id, token, etag),
          uploadGeometry: (id, file, token, etag) => api.uploadRouteGeometry(id, file, token, etag),
          putWaypoints: (id, wps, token, etag) => api.putRouteWaypoints(id, wps, token, etag),
          uploadMedia: (id, file, token, opts) => api.uploadMedia(id, file, token, opts),
          deleteMedia: (id, mediaId, token) => api.deleteMedia(id, mediaId, token),
        },
      })
      if (!isCurrentSaveAttempt() || (publicationAttempt != null && !isCurrentPublicationAttempt()))
        return

      if (result.success) {
        if (publishDirect && route.status === 'pending_review') {
          if (publicationAttempt == null) return
          try {
            const approved = await api.approveRoutePublish(route.id, auth.token, result.etag)
            if (!isCurrentPublicationAttempt() || approved.route.id !== routeId) return
            result.etag = approved.etag ?? result.etag
            result.route = approved.route
          } catch (err) {
            if (!isCurrentPublicationAttempt()) return
            showToast({ message: friendlyError(err, t), tone: 'error', durationMs: 8000 })
            return
          }
        }
        if (result.route) loader.setRoute(result.route, result.etag)
        if (result.waypoints) loader.applyWaypointCommit(result.etag, result.waypoints)
        if (geometryChanged) {
          // The server reparses the uploaded GPX (waypoints, stats); refresh the
          // canonical geometry so the read view reflects the committed track.
          setDraftTrackReversed(false)
          try {
            const { geometry: refreshed } = await api.getRouteGeometry(route.id, auth.token)
            if (!isCurrentSaveAttempt()) return
            loader.setGeometry(refreshed)
          } catch {
            if (!isCurrentSaveAttempt()) return
            // Non-fatal: geometry will refresh on the next load.
          }
        }
        let mediaRefreshFailure: string | null = null
        if (mediaChanged) {
          try {
            const nextMedia = await api.listMedia(route.id, undefined, auth.token)
            if (!isCurrentSaveAttempt()) return
            loader.setMedia(nextMedia)
          } catch (err) {
            if (!isCurrentSaveAttempt()) return
            mediaRefreshFailure = friendlyError(err, t)
          }
        }
        if ((requestReview || (publishDirect && route.status === 'draft')) && publicationAttempt) {
          try {
            const fresh = await api.getRoute(routeId, auth.token)
            if (!isCurrentPublicationAttempt() || fresh.route.id !== routeId) return
            loader.setRoute(fresh.route, fresh.etag)
            dispatch({ type: 'draft/reset-from-route', route: fresh.route })
            setPublicationAccepted(false)
            setPublicationConsent({
              route: fresh.route,
              etag: fresh.etag ?? fresh.route.etag,
              approveAfterRequest: publishDirect,
              attemptId: publicationAttempt,
              targetKey,
              navigationGeneration,
            })
            return
          } catch (err) {
            if (!isCurrentPublicationAttempt()) return
            showToast({ message: friendlyError(err, t), tone: 'error', durationMs: 8000 })
            return
          }
        }
        showToast({
          message: mediaRefreshFailure
            ? locale === 'zh'
              ? `已保存，但刷新媒体失败：${mediaRefreshFailure}`
              : `Saved, but failed to refresh media: ${mediaRefreshFailure}`
            : publishDirect
              ? locale === 'zh'
                ? '已发布'
                : 'Published'
              : (result.route?.status ?? route.status) === 'draft'
                ? locale === 'zh'
                  ? '草稿已保存，可继续编辑或提交审核'
                  : 'Saved as draft — you can keep editing or submit for review'
                : locale === 'zh'
                  ? '已保存'
                  : 'Saved',
          tone: mediaRefreshFailure ? 'error' : 'success',
          ...(mediaRefreshFailure ? { durationMs: 8000 } : {}),
        })
        stopEditing()
      } else {
        // Partial failure — update etag to latest successful, keep editing
        if (result.route) loader.setRoute(result.route, result.etag)
        if (result.waypoints) loader.applyWaypointCommit(result.etag, result.waypoints)
        const stepLabel = result.failedStep ?? 'unknown'
        const detail = result.failedError
          ? friendlyError(result.failedError, t)
          : (result.failedDetail ?? '')
        showToast({
          message: t(result.mutated ? 'routeDetail.partialSaveFailed' : 'routeDetail.saveFailed')
            .replace('{{step}}', stepLabel)
            .replace('{{detail}}', detail),
          tone: 'error',
          durationMs: 8000,
        })
        // Update media changes (successful items already spliced by commitAllEdits)
        setDraftMediaChanges({ ...draftMediaChanges })
      }
    } finally {
      if (isCurrentSaveAttempt()) {
        savingEditRef.current = false
        setSavingEdit(false)
      }
    }
  }

  async function deleteRoute() {
    if (!auth.token || !etag || !route) return
    if (!confirm(`Delete "${route.title}"?`)) return
    try {
      await withConflictRetry((e) => api.deleteRoute(route.id, auth.token!, e), etag, {
        reloadEtag,
      })
      navigate('/routes')
    } catch (err) {
      showToast({ message: friendlyError(err, t), tone: 'error', durationMs: 8000 })
    }
  }

  async function applyRouteMutation(
    updated: RoutePublic,
    nextEtag: string | null,
    navigationGeneration = navigationGenerationRef.current,
  ) {
    const isCurrentMutation = () => isCurrentRoute(updated.id, routeTargetKey, navigationGeneration)
    if (!isCurrentMutation()) return
    const refreshSeq = ++routeMutationRefreshSeq.current
    loader.setRoute(
      {
        ...updated,
        description_attribution:
          updated.status === 'published'
            ? (updated.description_attribution ?? route?.description_attribution)
            : undefined,
      },
      nextEtag,
    )
    dispatch({ type: 'draft/reset-from-route', route: updated })
    if (updated.status !== 'published') return
    try {
      // Mutation payloads omit read projections; only detail GET resolves attribution.
      const fresh = await api.getRoute(updated.id, auth.token)
      if (
        !isCurrentMutation() ||
        refreshSeq !== routeMutationRefreshSeq.current ||
        fresh.route.id !== updated.id
      )
        return
      loader.setRoute(fresh.route, fresh.etag)
    } catch (err) {
      if (!isCurrentMutation() || refreshSeq !== routeMutationRefreshSeq.current) return
      showToast({ message: friendlyError(err, t), tone: 'error', durationMs: 8000 })
    }
  }

  async function archiveRoute() {
    if (!auth.token || !etag || !route) return
    const navigationGeneration = navigationGenerationRef.current
    try {
      const { route: updated, etag: nextEtag } = await withConflictRetry(
        (e) => api.archiveRoute(route.id, auth.token!, e),
        etag,
        { reloadEtag },
      )
      await applyRouteMutation(updated, nextEtag ?? updated.etag, navigationGeneration)
    } catch (err) {
      showToast({ message: friendlyError(err, t), tone: 'error', durationMs: 8000 })
    }
  }

  async function restoreRoute() {
    if (!auth.token || !etag || !route) return
    const navigationGeneration = navigationGenerationRef.current
    try {
      const { route: updated, etag: nextEtag } = await withConflictRetry(
        (e) => api.restoreRoute(route.id, auth.token!, e),
        etag,
        { reloadEtag },
      )
      await applyRouteMutation(updated, nextEtag ?? updated.etag, navigationGeneration)
    } catch (err) {
      showToast({ message: friendlyError(err, t), tone: 'error', durationMs: 8000 })
    }
  }

  async function cancelReview() {
    if (!auth.token || !etag || !route) return
    const navigationGeneration = navigationGenerationRef.current
    try {
      const { route: updated, etag: nextEtag } = await withConflictRetry(
        (e) => api.cancelPublish(route.id, auth.token!, e),
        etag,
        { reloadEtag },
      )
      await applyRouteMutation(updated, nextEtag ?? updated.etag, navigationGeneration)
    } catch (err) {
      showToast({ message: friendlyError(err, t), tone: 'error', durationMs: 8000 })
    }
  }

  async function requestReviewFromReadMode() {
    if (!auth.token || !etag || !route) return
    const attemptId = ++consentAttemptRef.current
    const routeId = route.id
    const targetKey = routeTargetRef.current
    const navigationGeneration = navigationGenerationRef.current
    const isCurrentAttempt = () =>
      isCurrentConsentAttempt(attemptId, routeId, targetKey, navigationGeneration)
    try {
      const fresh = await api.getRoute(routeId, auth.token)
      if (!isCurrentAttempt() || fresh.route.id !== routeId) return
      loader.setRoute(fresh.route, fresh.etag)
      dispatch({ type: 'draft/reset-from-route', route: fresh.route })
      setPublicationAccepted(false)
      setPublicationConsent({
        route: fresh.route,
        etag: fresh.etag ?? fresh.route.etag,
        approveAfterRequest: false,
        attemptId,
        targetKey,
        navigationGeneration,
      })
    } catch (err) {
      if (!isCurrentAttempt()) return
      showToast({ message: friendlyError(err, t), tone: 'error', durationMs: 8000 })
    }
  }

  async function confirmPublicationConsent() {
    const consent = publicationConsent
    if (!consent || !publicationAccepted || !auth.token) return
    const isCurrentAttempt = () =>
      isCurrentConsentAttempt(
        consent.attemptId,
        consent.route.id,
        consent.targetKey,
        consent.navigationGeneration,
      )
    if (!isCurrentAttempt()) return
    setSavingEdit(true)
    try {
      const requested = await api.requestRoutePublish(consent.route.id, auth.token, consent.etag)
      if (!isCurrentAttempt() || requested.route.id !== consent.route.id) return
      let completed = requested
      if (consent.approveAfterRequest) {
        completed = await api.approveRoutePublish(
          consent.route.id,
          auth.token,
          requested.etag ?? requested.route.etag,
        )
        if (!isCurrentAttempt() || completed.route.id !== consent.route.id) return
      }
      if (!isCurrentAttempt() || completed.route.id !== consent.route.id) return
      void applyRouteMutation(
        completed.route,
        completed.etag ?? completed.route.etag,
        consent.navigationGeneration,
      )
      setPublicationConsent(null)
      setPublicationAccepted(false)
      showToast({
        message:
          completed.route.status === 'published'
            ? locale === 'zh'
              ? '已发布'
              : 'Published'
            : locale === 'zh'
              ? '已提交审核'
              : 'Submitted for review',
        tone: 'success',
      })
      if (editing) stopEditing()
    } catch (err) {
      if (!isCurrentAttempt()) return
      if (err instanceof ApiError && (err.status === 409 || err.status === 412)) {
        try {
          const fresh = await api.getRoute(consent.route.id, auth.token)
          if (isCurrentAttempt()) {
            loader.setRoute(fresh.route, fresh.etag)
            dispatch({ type: 'draft/reset-from-route', route: fresh.route })
          }
        } finally {
          if (isCurrentAttempt()) {
            setPublicationConsent(null)
            setPublicationAccepted(false)
            showToast({
              message:
                locale === 'zh'
                  ? '线路已更新，请重新审阅并确认许可'
                  : 'The route changed. Review it again before confirming.',
              tone: 'error',
              durationMs: 8000,
            })
          }
        }
        return
      }
      showToast({ message: friendlyError(err, t), tone: 'error', durationMs: 8000 })
    } finally {
      if (isCurrentAttempt()) {
        setSavingEdit(false)
      }
    }
  }

  async function commitWaypoints(next: EditableWaypoint[]): Promise<string | null> {
    if (!auth.token || !route || !etag || !serverGeometry) return null
    loader.setGeometry({ ...serverGeometry, points: toGeometryPoints(next) })
    setCommittingWaypoints(true)
    try {
      const result = await withConflictRetry(
        (e) => api.putRouteWaypoints(route.id, next.map(stripWaypoint), auth.token!, e),
        serverGeometry.etag ?? etag,
        { reloadEtag },
      )
      loader.applyWaypointCommit(result.etag, result.waypoints)
      return result.etag
    } catch (err) {
      showToast({ message: friendlyError(err, t), tone: 'error', durationMs: 8000 })
      void loader.reload()
      return null
    } finally {
      setCommittingWaypoints(false)
    }
  }

  function mergeSelectedAutoWaypoints() {
    if (!serverGeometry || draftGeometryPreview) return
    const selected = autoCandidates.filter((candidate) => selectedAutoIds.has(candidate.id))
    if (selected.length === 0) return
    const base = (draftWaypoints ?? serverGeometry.points) as EditableWaypoint[]
    const merged = mergeEditableWaypoints(base, selected)
    setDraftWaypoints(merged)
    setAutoCandidates([])
    setSelectedAutoIds(new Set())
  }

  async function onReplaceGpx(picked: File | null) {
    if (!picked || !route) return
    // Stage the replacement: parse a local preview line and keep the file for
    // the unified Save commit. No network call until Save.
    let text: string
    if (typeof picked.text === 'function') {
      text = await picked.text()
    } else {
      text = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(String(reader.result ?? ''))
        reader.onerror = () => reject(reader.error ?? new Error('read failed'))
        reader.readAsText(picked)
      })
    }
    let line: RouteLinePoint[]
    try {
      line = parseGpx(text)
    } catch (err) {
      if (!(err instanceof UnsupportedGpxSegmentsError)) throw err
      showToast({ message: friendlyError(err, t), tone: 'error', durationMs: 8000 })
      return
    }
    setReplacePreviewLine(line)
    setDraftGpxFile(picked)
    setDraftWaypoints(null)
    setDraftTrackReversed(false)
    setStagedDrawPreview(null)
    setDrawingBlankPreview(false)
    setPreDrawTrackDraft(null)
    autoWaypointRequestSeq.current += 1
    setAutoCandidates([])
    setSelectedAutoIds(new Set())
    setAddingWaypoint(false)
    setAddWaypointKindPickerOpen(false)
    setPendingAddWaypointKind(null)
    dispatch({ type: 'active/waypoint/clear' })
    dispatch({ type: 'focus/waypoint/clear' })
    dispatch({ type: 'pinned-waypoints/clear' })
    setTrackMode('upload')
  }

  // Editor reverse is a staged toggle: it flips the local draft direction and
  // previews the reversed track. The reversed GPX is built and persisted only
  // when the user confirms the unified Save (see handleConfirmSave).
  function toggleDraftReverse() {
    setDraftTrackReversed((prev) => !prev)
  }

  async function onMediaUpload(uploadFile: File) {
    if (!auth.token || !route) return
    setUploadingMedia(true)
    try {
      const created = await api.uploadMedia(route.id, uploadFile, auth.token)
      loader.appendMedia(created)
    } catch (err) {
      showToast({ message: friendlyError(err, t), tone: 'error', durationMs: 8000 })
    } finally {
      setUploadingMedia(false)
    }
  }

  async function onMediaDelete(mediaId: string) {
    if (!auth.token || !route) return
    if (!confirm(locale === 'zh' ? '删除这条媒体？' : 'Delete this media?')) return
    try {
      await api.deleteMedia(route.id, mediaId, auth.token)
      loader.removeMedia(mediaId)
    } catch (err) {
      showToast({ message: friendlyError(err, t), tone: 'error', durationMs: 8000 })
    }
  }

  async function onUploadWaypointMedia(waypointId: string, file: File) {
    if (!auth.token || !route) return
    try {
      const created = await api.uploadMedia(route.id, file, auth.token, { waypointId })
      loader.appendMedia(created)
    } catch (err) {
      showToast({ message: friendlyError(err, t), tone: 'error', durationMs: 8000 })
    }
  }

  async function onDeleteWaypointMedia(mediaId: string) {
    if (!auth.token || !route) return
    if (!confirm(locale === 'zh' ? '删除这张图片？' : 'Delete this image?')) return
    try {
      await api.deleteMedia(route.id, mediaId, auth.token)
      loader.removeMedia(mediaId)
    } catch (err) {
      showToast({ message: friendlyError(err, t), tone: 'error', durationMs: 8000 })
    }
  }

  function onWaypointAddRequest(result: { ok: true; lat: number; lon: number; ele?: number }) {
    if (!serverGeometry || draftGeometryPreview) return

    const newId = newWaypointId()
    const tempWp: EditableWaypoint = {
      id: newId,
      lat: result.lat,
      lon: result.lon,
      ...(result.ele !== undefined ? { ele: result.ele } : {}),
      desc: '',
      kind: pendingAddWaypointKind ?? 'generic',
      auto: false,
    }
    setDraftWaypoints((prev) => [
      ...(prev ?? (serverGeometry.points as EditableWaypoint[])),
      tempWp,
    ])
    dispatch({ type: 'active/waypoint', id: newId })
    dispatch({ type: 'focus/waypoint', id: newId })
    dispatch({ type: 'pinned-waypoints/add', id: newId })
    setAddingWaypoint(false)
    setPendingAddWaypointKind(null)
  }

  async function onAiAssist() {
    if (!auth.token || !route) {
      showToast({ message: t('editor.aiAssistError'), tone: 'info' })
      return
    }
    if (aiAssisting) return
    setAiAssisting(true)
    try {
      const stats =
        gpxStats && gpxStats.distanceKm > 0
          ? {
              pointCount: geometry?.line.length ?? 0,
              distanceKm: gpxStats.distanceKm,
              ascentM: gpxStats.ascentM,
              descentM: gpxStats.descentM,
            }
          : undefined
      const result = await api.enrichRouteRich(
        {
          title: draft.title || route.title,
          current_summary: draft.summary || undefined,
          stats,
          region: draft.region,
          natural_features: draft.natural_features,
          activities: draft.activities,
        },
        auth.token,
      )
      const patch: Partial<RoutePublic> = {}
      if (result.title_candidate?.title) patch.title = result.title_candidate.title
      if (result.summary || result.description_md) {
        patch.summary = (result.summary ?? result.description_md ?? '').trim()
      }
      if (result.difficulty) patch.difficulty = result.difficulty
      if (Object.keys(patch).length === 0) {
        showToast({ message: t('editor.aiAssistEmptySummary'), tone: 'info' })
      } else {
        const preview = [
          patch.title ? `${locale === 'zh' ? '标题' : 'Title'}: ${patch.title}` : '',
          patch.difficulty ? `${locale === 'zh' ? '难度' : 'Difficulty'}: ${patch.difficulty}` : '',
          patch.summary
            ? `\n${locale === 'zh' ? '描述' : 'Summary'}:\n${patch.summary.slice(0, 300)}${patch.summary.length > 300 ? '…' : ''}`
            : '',
        ]
          .filter(Boolean)
          .join('\n')
        if (
          window.confirm(
            `${locale === 'zh' ? 'AI 生成内容预览' : 'AI generated preview'}:\n\n${preview}\n\n${locale === 'zh' ? '确认应用？' : 'Apply?'}`,
          )
        ) {
          dispatch({ type: 'draft/merge', patch })
          showToast({ message: t('routeEditorSaved'), tone: 'success' })
          await loadAutoWaypoints(route.id)
        }
      }
    } catch (err) {
      showToast({
        message: `${t('editor.aiAssistError')}: ${friendlyError(err, t)}`,
        tone: 'error',
        durationMs: 8000,
      })
    } finally {
      setAiAssisting(false)
    }
  }

  async function onDetectRegion() {
    if (!auth.token) return
    const center = trackCentroid(geometry?.line ?? [])
    if (!center) {
      showToast({ message: t('editor.regionDetectMissing'), tone: 'info' })
      return
    }
    setRegionDetecting(true)
    try {
      const resp = await api.reverseGeocode(center, auth.token)
      const detected = resp.natural_features ?? []
      if (!resp.region && detected.length === 0) {
        showToast({ message: t('editor.regionDetectFailed'), tone: 'info' })
        return
      }
      dispatch({
        type: 'draft/merge',
        patch: {
          region: resp.region ?? draft.region,
          natural_features: mergeNaturalFeatures(draft.natural_features ?? [], detected),
        },
      })
      showToast({ message: t('editor.regionDetectAuto'), tone: 'success' })
    } catch {
      showToast({ message: t('editor.regionDetectFailed'), tone: 'info' })
    } finally {
      setRegionDetecting(false)
    }
  }

  function patchRegion(patch: Partial<NonNullable<RoutePublic['region']>>) {
    dispatch({
      type: 'draft/merge',
      patch: { region: compactRegion({ country: 'CN', ...draft.region, ...patch }) },
    })
  }

  const trackUploadActions = (
    <div className="flex flex-wrap items-center gap-2">
      <GpxFileActions
        geometry={geometry}
        downloadName={route.slug}
        routeId={route.id}
        token={auth.token ?? null}
        reversed={editing ? draftTrackReversed : trackReversed}
        onToggleReverse={
          editing ? (canRouteDirectEdit ? toggleDraftReverse : undefined) : toggleTrackReversed_
        }
        reverseDisabled={editing ? savingEdit || trackDrawActive : false}
        editorMode={editing}
        edit={
          editing && canRouteDirectEdit
            ? {
                uploading: uploadingGpx,
                hasGeometry: Boolean(geometry),
                onUpload: (uploadFile) => void onReplaceGpx(uploadFile),
              }
            : undefined
        }
      />
    </div>
  )

  return (
    <div
      className="flex flex-col overflow-y-auto xl:h-full xl:min-h-0 xl:overflow-hidden"
      data-testid="route-detail"
    >
      {shareOpen && (
        <ShareRouteModal
          route={route}
          geometry={geometry}
          shareUrl={shareUrl}
          onClose={() => setShareOpen(false)}
        />
      )}

      {publicationConsent && publicationConsent.targetKey === routeTargetKey && (
        <PublicationConsentDialog
          route={publicationConsent.route}
          etag={publicationConsent.etag}
          locale={locale}
          accepted={publicationAccepted}
          busy={savingEdit}
          onAcceptedChange={setPublicationAccepted}
          onClose={() => {
            consentAttemptRef.current += 1
            setPublicationConsent(null)
            setPublicationAccepted(false)
          }}
          onConfirm={() => void confirmPublicationConsent()}
        />
      )}

      <EditDiffPreview
        open={savePreview != null}
        route={route}
        draft={draftPatch()}
        serverWaypoints={serverGeometry?.points ?? []}
        draftWaypoints={savePreview ? savePreview.draftWaypoints : draftWaypoints}
        draftGpxFile={draftGpxFile}
        draftTrackReversed={draftTrackReversed}
        mediaChanges={draftMediaChanges}
        serverMedia={media}
        mediaToken={auth.status === 'authed' ? auth.token : null}
        statusAction={
          savePreview?.mode === 'publish'
            ? 'publish'
            : savePreview?.mode === 'request'
              ? 'request'
              : undefined
        }
        onClose={() => setSavePreview(null)}
        onConfirm={async () => {
          if (savingEditRef.current) return
          const preview = savePreview
          setSavePreview(null)
          await handleConfirmSave(
            preview?.mode === 'publish',
            preview ? preview.draftWaypoints : draftWaypoints,
            preview?.mode === 'request',
          )
        }}
        confirmDisabled={saveActionDisabled}
      />

      {shortcutHelpOpen && (
        <KeyboardShortcutDialog locale={locale} onClose={() => setShortcutHelpOpen(false)} />
      )}

      <div
        className={
          'grid gap-3 rounded-[var(--xr-radius-lg)] p-3 transition-colors xl:min-h-0 xl:flex-1 xl:grid-cols-[minmax(0,70fr)_minmax(0,30fr)] xl:overflow-hidden ' +
          (editing ? 'bg-[var(--xr-accent-wash)]' : '')
        }
        data-testid="route-detail-grid"
        data-editing={editing ? 'true' : undefined}
      >
        <div className="flex min-w-0 flex-col gap-2 xl:min-h-0 xl:overflow-hidden">
          <div className="relative h-[60svh] min-w-0 xl:h-auto xl:min-h-0 xl:flex-1">
            {trackDrawActive ? (
              <div className="relative h-full overflow-hidden border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)]">
                <DrawingMap
                  vertices={editDraw.vertices}
                  pointVertices={[]}
                  previewVertices={[]}
                  routedLine={editDraw.routedPreviewLine.map((p) => ({ lng: p.lon, lat: p.lat }))}
                  lineMode={true}
                  onAddVertex={editDraw.addVertex}
                  onBeginMoveVertex={editDraw.beginMoveVertex}
                  onEndMoveVertex={editDraw.endMoveVertex}
                  onBeginMovePointVertex={editDraw.beginMovePoi}
                  onEndMovePointVertex={editDraw.endMovePoi}
                  onInsertVertex={editDraw.insertVertex}
                  onMoveVertex={editDraw.updateVertex}
                  onMovePointVertex={editDraw.updatePoiPosition}
                  disabled={savingEdit}
                  {...(drawInitialCenter ? { initialCenter: drawInitialCenter } : {})}
                />
              </div>
            ) : (
              <>
                <CoverStage
                  geometry={geometry}
                  media={media}
                  mediaToken={auth.status === 'authed' ? auth.token : null}
                  stage={coverStage}
                  onStageChange={setCoverStage}
                  styleRequest={mapStyleRequest}
                  canEdit={canOpenEditor}
                  onWaypointsStateChange={onMapWaypointsStateChange}
                  focusWaypoint={focusWaypoint}
                  pinnedWaypoints={pinnedWaypoints}
                  activeWaypointId={activeWaypoint?.id ?? null}
                  onWaypointClick={activateWaypoint_}
                  attachmentWaypointIds={attachmentWaypointIds}
                  showWaypointsOnMap={showWaypointsOnMap}
                  focusMedia={focusMedia}
                  onMapStyleChange={setActiveMapStyle}
                  mapEditMode={editing && !draftGeometryPreview ? 'inline' : 'none'}
                  addingWaypoint={addingWaypoint && !draftGeometryPreview}
                  onWaypointAddRequest={onWaypointAddRequest}
                />
                {auth.status === 'authed' && route && (
                  <div className="absolute bottom-4 right-4 z-20">
                    <OfflineTileButton
                      routeId={route.id}
                      routeTitle={route.title}
                      geometry={geometry}
                      activeStyle={activeMapStyle}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="flex min-w-0 flex-col rounded-[var(--xr-radius-lg)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] xl:min-h-0 xl:overflow-hidden">
          <div
            role="tablist"
            aria-label={t('aria.content')}
            className="flex flex-none items-center gap-0 border-b border-[var(--xr-border-default)] px-2 py-1"
            data-testid="route-right-tabs"
          >
            <RightTabButton
              selected={rightTab === 'intro'}
              onClick={() => selectRightTab('intro')}
              label={locale === 'zh' ? '详情' : 'Details'}
              testid="route-right-tab-intro"
            />
            <RightTabButton
              selected={rightTab === 'history'}
              onClick={() => selectRightTab('history')}
              label={locale === 'zh' ? '协作' : 'Collab'}
              testid="route-right-tab-history"
            />
            <div className="ml-auto flex items-center gap-2 pr-1">
              {editing ? (
                <>
                  <Button
                    onClick={() =>
                      setSavePreview({ mode: 'review', draftWaypoints: waypointSnapshot() })
                    }
                    size="sm"
                    data-testid="route-edit-save"
                    disabled={saveActionDisabled}
                  >
                    {canRouteDirectEdit ? t('save') : locale === 'zh' ? '建议编辑' : 'Suggest edit'}
                  </Button>
                  {auth.user != null &&
                    canShowPublishDirectRoute(auth.user, route) &&
                    (route.status !== 'draft' || auth.user.id === route.created_by) && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                          setSavePreview({ mode: 'publish', draftWaypoints: waypointSnapshot() })
                        }
                        data-testid="route-edit-publish-direct"
                        disabled={saveActionDisabled}
                      >
                        {t('publishDirect')}
                      </Button>
                    )}
                  {auth.user?.id === route.created_by && route.status === 'draft' && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() =>
                        setSavePreview({ mode: 'request', draftWaypoints: waypointSnapshot() })
                      }
                      data-testid="route-edit-request-publish"
                      disabled={saveActionDisabled}
                    >
                      {locale === 'zh' ? '提交审核' : 'Request review'}
                    </Button>
                  )}
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={stopEditing}
                    disabled={savingEdit || uploadingGpx}
                  >
                    {t('cancel')}
                  </Button>
                  {canRouteDirectEdit && route.status === 'published' && (
                    <Button
                      variant="warning"
                      size="sm"
                      onClick={() => void archiveRoute()}
                      data-testid="route-edit-unpublish"
                    >
                      {locale === 'zh' ? '撤下' : 'Unpublish'}
                    </Button>
                  )}
                  {canRouteDirectEdit && route.status === 'archived' && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => void restoreRoute()}
                      data-testid="route-edit-restore"
                    >
                      {t('routeAction.restore')}
                    </Button>
                  )}
                  {isCreatorOrAdmin && (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => void deleteRoute()}
                      data-testid="route-edit-delete"
                    >
                      {locale === 'zh' ? '删除' : 'Delete'}
                    </Button>
                  )}
                </>
              ) : (
                <ActionMenu
                  label={locale === 'zh' ? '更多操作' : 'More actions'}
                  testid="route-actions"
                  triggerClassName="border-transparent bg-transparent shadow-none hover:bg-[var(--xr-bg-surface-elev)]"
                  items={[
                    {
                      key: 'share',
                      label: locale === 'zh' ? '分享' : 'Share',
                      icon: <IconShare />,
                      tone: 'accent',
                      onClick: () => setShareOpen(true),
                    },
                    ...(canOpenEditor
                      ? [
                          {
                            key: 'edit',
                            label: locale === 'zh' ? '编辑' : 'Edit',
                            icon: <IconPencil />,
                            onClick: startEditing,
                          },
                        ]
                      : []),
                    ...(route.status === 'pending_review' && !isCreatorPendingReview
                      ? [
                          {
                            key: 'review-publication',
                            label: t('routeAction.reviewPublication'),
                            icon: <IconPencil />,
                            tone: 'accent' as const,
                            onClick: () => dispatch({ type: 'right-tab/set', tab: 'history' }),
                          },
                        ]
                      : []),
                    ...(isCreatorDraft
                      ? [
                          {
                            key: 'request-review',
                            label: locale === 'zh' ? '提交审核' : 'Request review',
                            icon: <IconPencil />,
                            tone: 'accent' as const,
                            onClick: () => void requestReviewFromReadMode(),
                          },
                        ]
                      : []),
                    ...(isCreatorPendingReview
                      ? [
                          {
                            key: 'cancel-review',
                            label: t('routeAction.cancelReview'),
                            icon: <IconPencil />,
                            onClick: () => void cancelReview(),
                          },
                        ]
                      : []),
                  ]}
                />
              )}
            </div>
          </div>

          {editingPresenceUser && (
            <p
              className="flex-none border-b border-[var(--xr-border-default)] bg-[var(--xr-accent-soft)] px-3 py-2 text-xs text-[var(--xr-text-secondary)]"
              data-testid="route-presence-editing-hint"
            >
              {editingPresenceUser.display_name} 正在编辑路点
            </p>
          )}

          {rightTab === 'intro' && (
            <div className="flex flex-col gap-3 p-4 xl:min-h-0 xl:flex-1 xl:overflow-y-auto">
              {editing && committingWaypoints && (
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-[var(--xr-text-muted)]">
                    {locale === 'zh' ? '路点保存中…' : 'Saving waypoints…'}
                  </span>
                </div>
              )}

              <section
                className="flex flex-col gap-2.5 border-b border-[var(--xr-border-default)] pb-4"
                data-testid="intro-summary"
              >
                <div className="flex items-center justify-between gap-3">
                  {editing ? (
                    <Input
                      id="route-edit-title"
                      name="route-edit-title"
                      type="text"
                      aria-label={t('editor.routeTitle')}
                      value={draft.title ?? ''}
                      maxLength={200}
                      onChange={(event) =>
                        dispatch({ type: 'draft/merge', patch: { title: event.target.value } })
                      }
                      data-testid="route-detail-title"
                      className="!min-w-0 !border-transparent !bg-transparent !px-0 !py-0 !text-xl !font-semibold !tracking-tight !shadow-none !min-h-0 !h-auto hover:!bg-[var(--xr-bg-surface-elev)] focus:!border-[var(--xr-brand-primary)] focus:!bg-[var(--xr-bg-surface)] focus:!ring-2 focus:!ring-[var(--xr-brand-primary-soft)]"
                    />
                  ) : (
                    <h1
                      data-testid="route-detail-title"
                      className="min-w-0 text-xl font-semibold tracking-tight text-[var(--xr-text-primary)]"
                    >
                      {route.title}
                    </h1>
                  )}
                  <FavoriteHeartButton
                    routeId={route.id}
                    initial={favoriteIds.has(route.id)}
                    bare
                  />
                </div>

                {editing ? (
                  <EditorTagBar
                    status={draft.status ?? route.status}
                    activities={draft.activities ?? route.activities ?? ['hiking']}
                    difficulty={draft.difficulty ?? ''}
                    routeType={draft.route_type ?? ''}
                    region={draft.region}
                    naturalFeatures={draft.natural_features ?? []}
                    onChangeStatus={() => undefined}
                    onChangeActivities={(next) =>
                      dispatch({ type: 'draft/merge', patch: { activities: next } })
                    }
                    onChangeDifficulty={(next) =>
                      dispatch({
                        type: 'draft/merge',
                        patch: { difficulty: next === '' ? undefined : next },
                      })
                    }
                    onChangeRouteType={(next) =>
                      dispatch({
                        type: 'draft/merge',
                        patch: { route_type: next === '' ? undefined : next },
                      })
                    }
                    onPatchRegion={patchRegion}
                    onChangeNaturalFeatures={(next) =>
                      dispatch({ type: 'draft/merge', patch: { natural_features: next } })
                    }
                    onDetectRegion={onDetectRegion}
                    regionDetecting={regionDetecting}
                    regionDetectAvailable={Boolean(geometry && geometry.line.length > 0)}
                    adminStatusOptions={isAdmin}
                    showStatusChip={false}
                    seasonality={draft.seasonality}
                    onChangeSeasonality={
                      canEditSeasonality
                        ? (next) => dispatch({ type: 'draft/merge', patch: { seasonality: next } })
                        : undefined
                    }
                    onAddSoon={() =>
                      showToast({ message: t('editor.tagbar.addSoon'), tone: 'info' })
                    }
                  />
                ) : (
                  <div className="flex flex-wrap items-center gap-1.5">
                    {(route.activities ?? []).map((a) => (
                      <Badge key={a} tone="brand">
                        {a}
                      </Badge>
                    ))}
                    {route.difficulty && (
                      <Badge tone="neutral">
                        {t('difficultyLabel')} · {difficultyLabel(route.difficulty, t)}
                      </Badge>
                    )}
                    {route.route_type && (
                      <Badge tone="neutral">
                        {t(`enumLabel_route_type_${route.route_type}` as never)}
                      </Badge>
                    )}
                    {route.region?.province && (
                      <Badge tone="neutral">
                        {[route.region.province, route.region.city, route.region.district]
                          .filter(Boolean)
                          .join(' · ')}
                      </Badge>
                    )}
                    {(route.natural_features ?? []).map((feature, idx) => (
                      <Badge
                        key={String(feature.kind) + ':' + feature.name + ':' + idx}
                        tone={
                          feature.kind === 'lake' ||
                          feature.kind === 'river' ||
                          feature.kind === 'waterfall'
                            ? 'info'
                            : 'brand'
                        }
                      >
                        {feature.name}
                      </Badge>
                    ))}
                  </div>
                )}

                {editing ? (
                  <></>
                ) : (
                  <>
                    <RouteSeasonalityBlock seasonality={route.seasonality} />
                  </>
                )}

                <KpiStrip
                  distanceKm={
                    gpxStats && gpxStats.distanceKm > 0
                      ? gpxStats.distanceKm
                      : allowRouteKpiFallback
                        ? route.distance_km
                        : null
                  }
                  ascentM={
                    !previewHasElevation
                      ? null
                      : gpxStats && gpxStats.ascentM > 0
                        ? gpxStats.ascentM
                        : allowRouteKpiFallback
                          ? route.ascent_m
                          : null
                  }
                  descentM={
                    !previewHasElevation
                      ? null
                      : gpxStats && gpxStats.descentM > 0
                        ? gpxStats.descentM
                        : allowRouteKpiFallback
                          ? (route.descent_m ?? null)
                          : null
                  }
                  durationSeconds={route.duration_seconds}
                />
                {!editing && geometry && geometry.line.length > 0 && (
                  <WeatherStrip lat={geometry.line[0]!.lat} lon={geometry.line[0]!.lon} />
                )}
                {!editing && route.status === 'pending_review' && (
                  <div
                    className="rounded-[var(--xr-radius-md)] border border-[var(--xr-brand-primary)]/40 bg-[var(--xr-brand-primary-soft)] px-3 py-2 text-sm"
                    data-testid="pending-publication-notice"
                  >
                    <p className="font-medium text-[var(--xr-text-primary)]">
                      {t('routeDetail.pendingReviewNotice.title')}
                    </p>
                    <p className="mt-1 text-xs text-[var(--xr-text-secondary)]">
                      {t('routeDetail.pendingReviewNotice.body')}
                    </p>
                    <button
                      type="button"
                      className="mt-2 text-xs font-medium text-[var(--xr-brand-primary)] hover:underline"
                      onClick={() => dispatch({ type: 'right-tab/set', tab: 'history' })}
                      data-testid="pending-publication-open-collab"
                    >
                      {t('routeDetail.pendingReviewNotice.action')}
                    </button>
                  </div>
                )}
              </section>

              <section className="space-y-1.5" data-testid="intro-description">
                <div className="flex items-center justify-between gap-2">
                  <h2 className="text-sm font-semibold text-[var(--xr-text-primary)]">
                    {locale === 'zh' ? '描述' : 'Description'}
                  </h2>
                  {editing && !draftGeometryPreview && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => void onAiAssist()}
                      disabled={aiAssisting || !auth.token}
                      title={t('editor.aiAssistTitle')}
                      data-testid="route-editor-ai-assist"
                    >
                      {aiAssisting ? t('editor.aiAssistRunning') : t('editor.aiAssistButton')}
                    </Button>
                  )}
                </div>
                {editing ? (
                  <Textarea
                    id="route-edit-description"
                    name="route-edit-description"
                    aria-label={t('editor.description')}
                    value={draft.summary ?? ''}
                    rows={6}
                    maxLength={2000}
                    onChange={(event) =>
                      dispatch({ type: 'draft/merge', patch: { summary: event.target.value } })
                    }
                    className="resize-y rounded-[var(--xr-radius-md)]"
                  />
                ) : route.description_attribution ||
                  (route.summary && normalizeDescription(route.summary)) ? (
                  <p className="whitespace-pre-line text-sm leading-snug text-[var(--xr-text-secondary)] [&_+_p]:mt-1">
                    {[route.description_attribution, normalizeDescription(route.summary ?? '')]
                      .filter(Boolean)
                      .join('\n\n')}
                  </p>
                ) : (
                  <p className="text-sm text-[var(--xr-text-muted)]">
                    {locale === 'zh' ? '暂无线路介绍。' : 'No route description yet.'}
                  </p>
                )}
              </section>

              {editing ? (
                <EditableMediaSection
                  media={media}
                  mediaToken={auth.status === 'authed' ? auth.token : null}
                  uploading={false}
                  pendingUploads={draftMediaChanges.added}
                  removedIds={new Set(draftMediaChanges.removed)}
                  onAddFile={(file) => {
                    const id = crypto.randomUUID()
                    const previewUrl = URL.createObjectURL(file)
                    setDraftMediaChanges((prev) => ({
                      ...prev,
                      added: [...prev.added, { file, previewUrl, id }],
                    }))
                  }}
                  onMarkRemove={(mediaId) => {
                    setDraftMediaChanges((prev) => ({
                      ...prev,
                      removed: [...prev.removed, mediaId],
                    }))
                  }}
                  onRemovePending={(draftId) => {
                    setDraftMediaChanges((prev) => {
                      const item = prev.added.find((a) => a.id === draftId)
                      if (item) URL.revokeObjectURL(item.previewUrl)
                      return { ...prev, added: prev.added.filter((a) => a.id !== draftId) }
                    })
                  }}
                  onFocus={focusMedia_}
                />
              ) : (
                <RouteMediaStrip
                  media={media}
                  mediaToken={auth.status === 'authed' ? auth.token : null}
                  locale={locale}
                  onFocusMedia={focusMedia_}
                />
              )}

              {(previewHasElevation || geometryLoading || geometryError) && (
                <RouteElevationSection
                  geometry={baseGeometry}
                  loading={geometryLoading}
                  error={geometryError}
                  hasElevation={previewHasElevation}
                />
              )}

              {(geometry || editing) && (
                <>
                  <section
                    className="space-y-3 border-t border-[var(--xr-border-default)] pt-4"
                    data-testid="intro-track-files"
                  >
                    <h2 className="text-sm font-semibold text-[var(--xr-text-primary)]">
                      {locale === 'zh' ? '轨迹文件' : 'Track files'}
                    </h2>
                    {canDrawTrack ? (
                      <EditorTrackSection
                        mode={trackMode}
                        onModeChange={enterTrackMode}
                        disabled={savingEdit}
                        upload={trackUploadActions}
                        draw={
                          <TrackDrawPanel
                            draw={editDraw}
                            t={drawT}
                            disabled={savingEdit}
                            onFinish={finishTrackDraw}
                            onCancel={() => enterTrackMode('upload')}
                            cancelLabel={t('cancel')}
                            showActivity={false}
                            showRouting
                            showName={false}
                          />
                        }
                      />
                    ) : (
                      trackUploadActions
                    )}
                  </section>
                  {geometry && (
                    <section className="space-y-2" data-testid="intro-waypoints">
                      <div className="flex flex-none items-center justify-between gap-2 text-xs text-[var(--xr-text-muted)]">
                        <span>
                          {t('statWaypoints')} · {geometry.points.length}
                        </span>
                        <div className="flex flex-none items-center gap-3">
                          <label className="inline-flex flex-none cursor-pointer items-center gap-1.5 select-none">
                            <input
                              id={editing ? 'waypoint-editor-show-on-map' : 'waypoint-show-on-map'}
                              name={
                                editing ? 'waypoint-editor-show-on-map' : 'waypoint-show-on-map'
                              }
                              type="checkbox"
                              checked={Boolean(showWaypointsOnMap)}
                              onChange={(e) =>
                                dispatch({ type: 'show-waypoints/set', show: e.target.checked })
                              }
                              className="h-3.5 w-3.5 cursor-pointer accent-[var(--xr-brand-primary)]"
                              data-testid={
                                editing ? 'waypoint-editor-show-on-map' : 'waypoint-show-on-map'
                              }
                            />
                            <span>{t('waypointsShowAll')}</span>
                          </label>
                          {editing && (
                            <Button
                              size="sm"
                              variant={addingWaypoint ? 'primary' : 'secondary'}
                              onClick={() => {
                                if (addingWaypoint) {
                                  setAddingWaypoint(false)
                                  setPendingAddWaypointKind(null)
                                  return
                                }
                                setAddWaypointKindPickerOpen(true)
                              }}
                              data-testid="waypoint-add-on-map"
                            >
                              {addingWaypoint
                                ? t('editor.waypointList.addRowActive')
                                : t('editor.waypointList.addRow')}
                            </Button>
                          )}
                        </div>
                      </div>
                      {editing && !draftGeometryPreview && autoCandidates.length > 0 && (
                        <AutoWaypointChecklist
                          candidates={autoCandidates}
                          selectedIds={selectedAutoIds}
                          onToggle={(id, checked) => {
                            setSelectedAutoIds((prev) => {
                              const next = new Set(prev)
                              if (checked) next.add(id)
                              else next.delete(id)
                              return next
                            })
                          }}
                          onMerge={mergeSelectedAutoWaypoints}
                        />
                      )}
                      <WaypointList
                        mode={editing && !draftGeometryPreview ? 'edit' : 'view'}
                        waypoints={
                          (editing
                            ? drawingBlankPreview
                              ? geometry.points
                              : (draftWaypoints ?? geometry.points)
                            : geometry.points) as EditableWaypoint[]
                        }
                        track={geometry.line}
                        activeWaypointId={activeWaypoint?.id}
                        selectedWaypointIds={pinnedWaypoints}
                        showAllWaypoints={showWaypointsOnMap}
                        onWaypointClick={(id) => {
                          setCoverStage('map')
                          activateWaypoint_(id)
                        }}
                        onWaypointsChange={
                          editing && !draftGeometryPreview
                            ? (next) => setDraftWaypoints(next)
                            : undefined
                        }
                        mediaByWaypointId={groupedMedia.byWaypointId}
                        mediaToken={auth.status === 'authed' ? auth.token : null}
                        onUploadWaypointMedia={editing ? onUploadWaypointMedia : undefined}
                        onDeleteWaypointMedia={editing ? onDeleteWaypointMedia : undefined}
                      />
                      {editing && route && !draftGeometryPreview && (
                        <WaypointKindPicker
                          open={addWaypointKindPickerOpen}
                          current={pendingAddWaypointKind ?? undefined}
                          presentation="modal"
                          onSelect={(kind) => {
                            setPendingAddWaypointKind(kind)
                            setAddWaypointKindPickerOpen(false)
                            setAddingWaypoint(true)
                          }}
                          onClose={() => setAddWaypointKindPickerOpen(false)}
                        />
                      )}
                    </section>
                  )}
                </>
              )}
            </div>
          )}
          {rightTab === 'history' && (
            <div className="flex flex-col gap-3 p-4 xl:min-h-0 xl:flex-1 xl:overflow-y-auto">
              <RouteCollabPanel
                routeId={route.id}
                route={route}
                routeWaypoints={geometry?.points ?? []}
                routeMedia={media}
                routeCreatorId={route.created_by}
                routeCreatorName={safeRouteCreatorName(route)}
                routeRegion={route.region}
                routeCreatedAt={route.created_at}
                etag={etag ?? route.etag}
                token={auth.token ?? null}
                onRevert={(updated) => {
                  void applyRouteMutation(updated.route, updated.route.etag)
                }}
                onRouteUpdate={(updatedRoute) => {
                  void applyRouteMutation(updatedRoute, updatedRoute.etag)
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// KpiStrip — single-row objective metric summary for the RouteDetail intro
// tab. Per `wiki/decisions/06-design-system.md` §Route detail intro this is
// the *only* place distance / ascent / descent appear; the elevation chart
// caption owns min/max/max-grade, and there is no "Time" / "Moving time" /
// "Avg speed" because those depend on who's walking the route, not the
// route itself.
function KpiStrip({
  distanceKm,
  ascentM,
  descentM,
  durationSeconds,
}: {
  distanceKm: number | null | undefined
  ascentM: number | null | undefined
  descentM: number | null | undefined
  durationSeconds?: number | null | undefined
}) {
  const hasDuration = durationSeconds != null && durationSeconds > 0
  const cols = hasDuration ? 'grid-cols-4' : 'grid-cols-3'
  return (
    <div
      data-testid="route-kpi-strip"
      role="group"
      aria-label="route stats"
      className={`grid w-full ${cols} divide-x divide-[var(--xr-border-default)] overflow-hidden rounded-[var(--xr-radius-md)] bg-[var(--xr-bg-surface-elev)]`}
    >
      <KpiPair value={distanceKm == null ? '—' : distanceKm.toFixed(2)} unit="km" />
      <KpiPair arrow="↑" value={ascentM == null ? '—' : `${Math.round(ascentM)}`} unit="m" />
      <KpiPair arrow="↓" value={descentM == null ? '—' : `${Math.round(descentM)}`} unit="m" />
      {hasDuration && <KpiPair value={formatDuration(durationSeconds!)} unit="" />}
    </div>
  )
}

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600)
  const m = Math.round((seconds % 3600) / 60)
  if (h > 0 && m > 0) return `${h}h${m}m`
  if (h > 0) return `${h}h`
  return `${m}m`
}

function KpiPair({ arrow, value, unit }: { arrow?: string; value: string; unit: string }) {
  const label = arrow ? `${arrow} ${value} ${unit}` : `${value} ${unit}`
  return (
    <span
      className="inline-flex items-baseline justify-center gap-1 px-2 py-2 text-base font-semibold text-[var(--xr-text-primary)]"
      aria-label={label}
    >
      {arrow && (
        <span className="text-sm font-semibold text-[var(--xr-text-muted)]" aria-hidden="true">
          {arrow}
        </span>
      )}
      <span>{value}</span>
      <span className="text-xs font-normal text-[var(--xr-text-muted)]">{unit}</span>
    </span>
  )
}

type ActivityValue = RoutePublic['activities'][number]
type AutoWaypointCandidate = WaypointReplace & { id?: string; auto?: boolean }

function stripWaypoint(waypoint: EditableWaypoint | AutoWaypointCandidate): WaypointReplace {
  return waypointToReplaceRequest(waypoint)
}

function toGeometryPoints(points: readonly EditableWaypoint[]): GeometryResponse['points'] {
  return points.map((point) => ({ ...point }))
}

function toEditableWaypoint(waypoint: AutoWaypointCandidate): EditableWaypoint {
  return {
    ...waypoint,
    id: waypoint.id ?? newWaypointId(),
  }
}

function mergeEditableWaypoints(
  existing: EditableWaypoint[],
  accepted: EditableWaypoint[],
): EditableWaypoint[] {
  return mergeWaypointRows(existing.map(stripWaypoint), accepted.map(stripWaypoint)).map((wp) =>
    toEditableWaypoint(wp),
  )
}

function mergeWaypointRows(
  existing: WaypointReplace[],
  accepted: WaypointReplace[],
): WaypointReplace[] {
  const out = [...existing]
  for (const wp of accepted) {
    if (
      out.some(
        (existingWp) =>
          Math.abs(existingWp.lat - wp.lat) < 0.000001 &&
          Math.abs(existingWp.lon - wp.lon) < 0.000001 &&
          (existingWp.name ?? '') === (wp.name ?? ''),
      )
    ) {
      continue
    }
    out.push(wp)
  }
  return out
}

function AutoWaypointChecklist({
  candidates,
  selectedIds,
  onToggle,
  onMerge,
}: {
  candidates: EditableWaypoint[]
  selectedIds: Set<string>
  onToggle: (id: string, checked: boolean) => void
  onMerge: () => void
}) {
  const { t } = useLocale()
  return (
    <section
      className="space-y-3 rounded-[var(--xr-radius-md)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface-elev)] p-3"
      data-testid="auto-waypoint-checklist"
    >
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3 className="text-xs font-semibold text-[var(--xr-text-primary)]">
            {t('editor.autoWaypoints.title')}
          </h3>
          <p className="text-[10px] text-[var(--xr-text-muted)]">
            {t('editor.autoWaypoints.hint')}
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={onMerge} disabled={selectedIds.size === 0}>
          {t('editor.autoWaypoints.addSelected')}
        </Button>
      </div>
      <div className="space-y-1.5">
        {candidates.map((candidate) => (
          <label
            key={candidate.id}
            className="flex items-start gap-2 rounded-[var(--xr-radius-sm)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] p-2 text-xs"
          >
            <input
              type="checkbox"
              className="mt-0.5"
              checked={selectedIds.has(candidate.id)}
              onChange={(event) => onToggle(candidate.id, event.target.checked)}
            />
            <span className="flex-1">
              <span className="font-medium text-[var(--xr-text-primary)]">
                {candidate.name?.trim() || t('editor.waypointList.namePlaceholder')}
              </span>
              <span className="ml-2 text-[var(--xr-text-muted)]">
                {candidate.kind && candidate.kind !== 'generic'
                  ? t(`waypointKind.${candidate.kind}` as never)
                  : candidate.sym
                    ? (t as (k: string, opts?: Record<string, unknown>) => string)(
                        `waypointKind.${candidate.sym.toLowerCase()}`,
                        { defaultValue: candidate.sym },
                      )
                    : t('editor.waypointList.typeGeneric')}
              </span>
              <span className="block font-mono text-[10px] text-[var(--xr-text-muted)]">
                {candidate.lat.toFixed(5)}, {candidate.lon.toFixed(5)}
                {candidate.ele !== undefined ? ` · ${Math.round(candidate.ele)} m` : ''}
              </span>
            </span>
          </label>
        ))}
      </div>
    </section>
  )
}

function trackCentroid(
  track: readonly { lat: number; lon: number }[],
): { lat: number; lon: number } | null {
  if (track.length === 0) return null
  let sumLat = 0
  let sumLon = 0
  for (const point of track) {
    sumLat += point.lat
    sumLon += point.lon
  }
  return { lat: sumLat / track.length, lon: sumLon / track.length }
}

function compactRegion(
  region: NonNullable<RoutePublic['region']>,
): RoutePublic['region'] | undefined {
  const trimmed = {
    country: region.country,
    province: region.province?.trim() || undefined,
    city: region.city?.trim() || undefined,
    district: region.district?.trim() || undefined,
  }
  if (!trimmed.province && !trimmed.city && !trimmed.district) return undefined
  return trimmed
}

function mergeNaturalFeatures(
  existing: NonNullable<RoutePublic['natural_features']>,
  detected: NonNullable<RoutePublic['natural_features']>,
): NonNullable<RoutePublic['natural_features']> {
  const out = [...existing]
  for (const feature of detected) {
    const name = feature.name.trim()
    if (!name) continue
    if (out.some((item) => item.kind === feature.kind && item.name === name)) continue
    out.push({ kind: feature.kind, name })
  }
  return out
}

function difficultyLabel(
  d: RoutePublic['difficulty'],
  t: ReturnType<typeof useLocale>['t'],
): string {
  switch (d) {
    case 'easy':
      return t('diffEasy')
    case 'moderate':
      return t('diffModerate')
    case 'hard':
      return t('diffHard')
    case 'expert':
      return t('diffExpert')
    default:
      return ''
  }
}

function PublicationConsentDialog({
  route,
  etag,
  locale,
  accepted,
  busy,
  onAcceptedChange,
  onClose,
  onConfirm,
}: {
  route: RoutePublic
  etag: string
  locale: string
  accepted: boolean
  busy: boolean
  onAcceptedChange: (accepted: boolean) => void
  onClose: () => void
  onConfirm: () => void
}) {
  const zh = locale === 'zh'
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-[var(--xr-overlay-bg)] p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="publication-consent-title"
      data-testid="publication-consent"
    >
      <div className="w-full max-w-lg space-y-4 rounded-[var(--xr-radius-lg)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] p-5 shadow-[var(--xr-shadow-md)]">
        <div>
          <h2
            id="publication-consent-title"
            className="text-lg font-semibold text-[var(--xr-text-primary)]"
          >
            {zh ? '确认提交发布审核' : 'Confirm publication review'}
          </h2>
          <p className="mt-1 text-sm text-[var(--xr-text-secondary)]">
            {zh
              ? '请基于刚刚重新载入的线路版本确认。'
              : 'Confirm against the route version that was just reloaded.'}
          </p>
        </div>
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 rounded-[var(--xr-radius-md)] bg-[var(--xr-bg-surface-elev)] p-3 text-sm">
          <dt className="text-[var(--xr-text-muted)]">{zh ? '线路' : 'Route'}</dt>
          <dd className="font-medium text-[var(--xr-text-primary)]">{route.title}</dd>
          <dt className="text-[var(--xr-text-muted)]">{zh ? '摘要' : 'Summary'}</dt>
          <dd className="text-[var(--xr-text-secondary)]">{route.summary || '—'}</dd>
          <dt className="text-[var(--xr-text-muted)]">{zh ? '状态' : 'Status'}</dt>
          <dd className="text-[var(--xr-text-secondary)]">{route.status}</dd>
          <dt className="text-[var(--xr-text-muted)]">{zh ? '版本' : 'Version'}</dt>
          <dd className="font-mono text-xs text-[var(--xr-text-secondary)]">{etag}</dd>
        </dl>
        <label className="flex items-start gap-3 text-sm text-[var(--xr-text-primary)]">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(event) => onAcceptedChange(event.target.checked)}
            className="mt-1"
            data-testid="publication-consent-checkbox"
          />
          <span>
            {zh
              ? '我确认对本次提交的线路目录数据（标题、摘要/说明、活动与难度等元数据、轨迹几何及途经点）拥有相应权利，并授予丁新宇（个人开发者）/寻径为公开线路目录所需的长期、免费、非独占许可；我保留原有权利。此许可不包括独立上传的照片、视频或其他媒体；线路审核发布后，即使账号注销，平台仍可依据该许可保留上述目录数据的去标识化公开副本。'
              : 'I confirm I hold the necessary rights to this submission’s route catalog data (title, summary/description, activity and difficulty metadata, route geometry, and waypoints) and grant Ding Xinyu (individual developer)/XingRoute a long-term, royalty-free, non-exclusive license needed for the public route catalog. I retain my rights. This license excludes independently uploaded photos, videos, and other media; after publication, a de-identified copy of the catalog data may remain after account deletion.'}{' '}
            <a className="text-[var(--xr-brand-primary)] underline" href="/terms">
              {zh ? '服务条款' : 'Terms'}
            </a>
            {' · '}
            <a className="text-[var(--xr-brand-primary)] underline" href="/privacy">
              {zh ? '隐私政策' : 'Privacy Policy'}
            </a>
          </span>
        </label>
        <p className="text-xs text-[var(--xr-text-muted)]">
          {zh ? '许可版本：2026-08-24' : 'License version: 2026-08-24'}
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            {zh ? '返回编辑' : 'Back to editor'}
          </Button>
          <Button
            onClick={onConfirm}
            disabled={!accepted || busy}
            data-testid="publication-consent-confirm"
          >
            {busy ? (zh ? '提交中…' : 'Submitting…') : zh ? '确认并提交' : 'Confirm and submit'}
          </Button>
        </div>
      </div>
    </div>
  )
}

function KeyboardShortcutDialog({ locale, onClose }: { locale: string; onClose: () => void }) {
  const { t } = useLocale()
  const rows =
    locale === 'zh'
      ? [
          ['m', '切换地图 / 媒体'],
          ['1 · 2 · 3', '切换标准 / 户外 / 卫星底图'],
          ['[ · ]', '上一个 / 下一个 waypoint'],
          ['e', '进入线路编辑'],
          ['Esc', '退出编辑或关闭本面板'],
          ['/', '打开线路标题搜索'],
          ['?', '显示快捷键'],
        ]
      : [
          ['m', 'Toggle map / media'],
          ['1 · 2 · 3', 'Switch standard / outdoor / satellite map style'],
          ['[ · ]', 'Previous / next waypoint'],
          ['e', 'Enter route editing'],
          ['Esc', 'Leave editing or close this panel'],
          ['/', 'Open route title search'],
          ['?', 'Show shortcuts'],
        ]
  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-[var(--xr-overlay-bg)] p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('aria.keyboardShortcuts')}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div className="w-full max-w-md rounded-[var(--xr-radius-lg)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] p-5 shadow-[var(--xr-shadow-md)]">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-[var(--xr-text-primary)]">
            {locale === 'zh' ? '键盘快捷键' : 'Keyboard shortcuts'}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Esc
          </Button>
        </div>
        <dl className="space-y-2">
          {rows.map(([keys, label]) => (
            <div key={keys} className="flex items-center justify-between gap-4 text-sm">
              <dt className="flex-none rounded-[var(--xr-radius-sm)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface-elev)] px-2 py-1 font-mono text-xs text-[var(--xr-text-primary)]">
                {keys}
              </dt>
              <dd className="text-right text-[var(--xr-text-secondary)]">{label}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  )
}

function RouteMediaStrip({
  media,
  mediaToken,
  locale,
  onFocusMedia,
}: {
  media: MediaAssetPublic[]
  mediaToken?: string | null
  locale: string
  onFocusMedia: (mediaId: string) => void
}) {
  return (
    <section className="space-y-2" data-testid="intro-media-section">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-[var(--xr-text-primary)]">
          {locale === 'zh' ? `关联图片 · ${media.length}` : `Associated media · ${media.length}`}
        </h2>
      </div>
      {media.length > 0 ? (
        <div className="grid grid-cols-4 gap-1.5">
          {media.slice(0, 8).map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => onFocusMedia(m.id)}
              className="aspect-square overflow-hidden rounded-[var(--xr-radius-sm)] border border-transparent transition hover:border-[var(--xr-brand-primary)] focus-visible:outline-none focus-visible:border-[var(--xr-brand-primary)]"
              data-testid="intro-media-thumb"
              aria-label={m.alt_text || ''}
            >
              {m.kind === 'image' ? (
                <AuthorizedMediaImage
                  source={m.thumb_url ?? m.blob_url}
                  token={mediaToken}
                  alt={m.alt_text || ''}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-[var(--xr-bg-surface-elev)] text-[10px] text-[var(--xr-text-muted)]">
                  ▶
                </div>
              )}
            </button>
          ))}
        </div>
      ) : (
        <p className="text-[11px] text-[var(--xr-text-muted)]">
          {locale === 'zh'
            ? '暂无关联图片。请上传媒体补充内容。'
            : 'No associated media yet. Upload some to add context.'}
        </p>
      )}
    </section>
  )
}

function routeShareUrl(route: RoutePublic | null): string {
  if (!route || typeof window === 'undefined') return ''
  return `${window.location.origin}/r/${route.slug}`
}

function safeRouteCreatorName(route: RoutePublic): string | undefined {
  const name = route.creator_name?.trim()
  if (name) return name
  const username = route.creator_username?.trim()
  return username && !isReservedUsername(username) ? username : undefined
}

function IconShare() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <path d="m8.6 10.6 6.8-4.2M8.6 13.4l6.8 4.2" />
    </svg>
  )
}

function RightTabButton({
  selected,
  onClick,
  label,
  testid,
}: {
  selected: boolean
  onClick: () => void
  label: string
  testid?: string
}) {
  // Underlined tab — same h-[30px] chrome as CoverStage's TabButton so the
  // left (Map / Gallery) and right (Intro / Details / History) strips
  // align across the page.
  const base =
    'inline-flex h-[30px] flex-none items-center px-3 text-xs font-medium transition border-b-2 -mb-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--xr-brand-primary)]'
  const cls = selected
    ? `${base} border-[var(--xr-brand-primary)] text-[var(--xr-brand-primary)]`
    : `${base} border-transparent text-[var(--xr-text-secondary)] hover:text-[var(--xr-text-primary)]`
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      onClick={onClick}
      className={cls}
      data-testid={testid}
    >
      <span>{label}</span>
    </button>
  )
}
